import { useEffect, useState } from "react";
import { getPlans, Periodicity } from "@/service/plans";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { selectPlan } from "@/redux/slices/plan";
import { useNavigate } from "react-router-dom";
import { Notyf } from "notyf";
import { loginWithToken } from "@/service/user";
import { login } from "@/redux/slices/auth";
import { consumeCheckoutLoginToken } from "@/utils/checkoutLoginToken";
import BrandHeader from "@/components/brandHeader";
import CompaniesModal from "@/components/companiesModal";
import {
  PlanCompactCard,
  PlanHeroCard,
  PlanRow,
} from "@/components/planCards";
import { Companies, PricingTier } from "@/redux/slices/plan";

export type { PricingTier, Companies };

export default function PricingPage() {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const tenant = useAppSelector((state) => state.tenant.loginTenant);
  const [loading, setLoading] = useState(true);
  const [planData, setPlanData] = useState<PricingTier[] | null>(null);
  const [companiesModal, setCompaniesModal] = useState<Companies[] | null>(
    null
  );

  const getAllPlans = async () => {
    return await getPlans()
      .then((data) => {
        if (data) {
          const dataMounted: PricingTier[] = data.map((plan) => ({
            name: plan.PlanName,
            id: plan.IdSubscriptionDetail,
            galaxPayId: plan.GalaxPayId,
            price: plan.Value,
            description: plan.Description,
            allCompanies: plan.AllCompanies,
            listCompanies: plan.companiesList,
            services: plan.subscriptionServiceList,
            periodicity: plan.Periodicity as Periodicity,
          }));

          setPlanData(dataMounted);
          return dataMounted;
        }
      })
      .catch(() => null);
  };

  const findUser = async (token: string) => {
    return await loginWithToken(token)
      .then((userData) => {
        if (userData.Token) {
          dispatch(login(userData));
          return userData;
        }
      })
      .catch(() => {
        const notyf = new Notyf({
          duration: 3000,
          position: { x: "center", y: "bottom" },
        });
        notyf.error("Usuário não encontrado !");
        return null;
      });
  };

  const selectPlanByLink = async (allPlans: PricingTier[], planId: number) => {
    if (planId) {
      const plan = allPlans?.find((data) => data.id === planId);
      if (plan) {
        await dispatch(selectPlan(plan));
        navigation("/checkout");
      }
    }
  };

  const initScreen = async () => {
    setLoading(true);
    const token = consumeCheckoutLoginToken();
    const allPlans = await getAllPlans();
    if (allPlans && token) {
      const user = await findUser(token);
      if (user && user.user.PlanId) {
        await selectPlanByLink(allPlans, user.user.PlanId);
      }
    }
    setLoading(false);
  };

  const onSubscribe = (plan: PricingTier) => {
    dispatch(selectPlan(plan));
    navigation("/checkout");
  };

  const onShowCompanies = (plan: PricingTier) => {
    setCompaniesModal(plan.listCompanies || []);
  };

  useEffect(() => {
    initScreen();
  }, []);

  const hero = planData?.[0];
  const compact = planData?.slice(1, 3) || [];

  return (
    <div className="flex min-h-[100dvh] w-full flex-col pb-28">
      <CompaniesModal
        companies={companiesModal}
        onClose={() => setCompaniesModal(null)}
      />

      <header className="mx-auto flex w-full max-w-7xl items-center px-6 py-8">
        <BrandHeader />
      </header>

      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <span className="h-10 w-10 rounded-full border-2 border-checkout-hairline border-t-[var(--tenant-accent-color)] animate-spin" />
        </div>
      ) : planData && hero ? (
        <main className="mx-auto w-full max-w-7xl px-6">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-checkout-muted">
              {tenant?.Name || "Planos"}
            </p>
            <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight md:text-6xl">
              Planos mais assinados
            </h1>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-12">
            <PlanHeroCard
              plan={hero}
              className={compact.length === 0 ? "md:col-span-12" : undefined}
              onSubscribe={onSubscribe}
              onShowCompanies={onShowCompanies}
            />
            {compact.length > 0 && (
              <div className="flex flex-col gap-4 md:col-span-5">
                {compact.map((plan) => (
                  <PlanCompactCard
                    key={plan.id}
                    plan={plan}
                    onSubscribe={onSubscribe}
                    onShowCompanies={onShowCompanies}
                  />
                ))}
              </div>
            )}
          </div>

          {planData.length > 0 && (
            <section className="mt-20">
              <div className="mb-8 flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-checkout-muted">
                    Catálogo
                  </p>
                  <h2 className="mt-2 font-display text-3xl font-semibold">
                    Todos os planos
                  </h2>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                {planData.map((plan) => (
                  <PlanRow
                    key={`all-${plan.id}`}
                    plan={plan}
                    onSubscribe={onSubscribe}
                    onShowCompanies={onShowCompanies}
                  />
                ))}
              </div>
            </section>
          )}
        </main>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <h1 className="font-display text-3xl font-semibold">
            Nenhum plano disponível
          </h1>
          <p className="mt-3 max-w-md text-checkout-muted">
            Esta barbearia ainda não publicou planos de assinatura.
          </p>
        </div>
      )}
    </div>
  );
}
